import {
  Card,
  Group,
  MultiSelect,
  Select,
  Stack,
  Switch,
  Tooltip,
} from '@mantine/core';
import { ChevronDown, Filter, Rows3 } from 'lucide-react';
import { useMemo } from 'react';
import {
  TREND_LOW_PASS_TOOLTIP,
  TREND_NORMALIZATION_TOOLTIP,
} from '../../../shared/trend-processing';
import {
  OVERLAY_SELECT_ALL_VALUE,
  TREND_FEATURE_LABELS,
  TREND_SIGNAL_KEYS,
} from '../constants';
import type { TrendSignalKey } from '../types';

type TrendChartControlsProps = {
  selectedFeature: TrendSignalKey;
  onFeatureChange: (feature: TrendSignalKey) => void;
  normalizationEnabled: boolean;
  onNormalizationChange: (value: boolean) => void;
  lowPassEnabled: boolean;
  onLowPassChange: (value: boolean) => void;
  showOverlaySessions: boolean;
  onShowOverlayChange: (value: boolean) => void;
  overlaySessionSelectData: Array<{ value: string; label: string }>;
  overlayMultiSelectValue: string[];
  onOverlaySessionChange: (values: string[]) => void;
};

export function TrendChartControls({
  selectedFeature,
  onFeatureChange,
  normalizationEnabled,
  onNormalizationChange,
  lowPassEnabled,
  onLowPassChange,
  showOverlaySessions,
  onShowOverlayChange,
  overlaySessionSelectData,
  overlayMultiSelectValue,
  onOverlaySessionChange,
}: TrendChartControlsProps) {
  const overlayCompactValueLabel = useMemo(() => {
    if (overlayMultiSelectValue.length <= 3) return undefined;

    const visibleLabels = overlayMultiSelectValue
      .slice(0, 2)
      .map(value => overlaySessionSelectData.find(item => item.value === value))
      .map(item => item?.label)
      .filter(Boolean);

    const hiddenCount = overlayMultiSelectValue.length - visibleLabels.length;

    return `${visibleLabels.join(', ')} (+${hiddenCount} altre)`;
  }, [overlayMultiSelectValue, overlaySessionSelectData]);

  const shouldCompactOverlayPills = overlayMultiSelectValue.length > 3;

  return (
    <Card withBorder padding='sm'>
      <Group gap='md' align='flex-end' wrap='wrap'>
        <Stack gap={4}>
          <Select
            label='Segnale'
            placeholder='Segnale'
            variant='filled'
            leftSection={<Rows3 size={14} />}
            rightSection={<ChevronDown size={14} />}
            data={TREND_SIGNAL_KEYS.map(feature => ({
              value: feature,
              label: TREND_FEATURE_LABELS[feature],
            }))}
            value={selectedFeature}
            onChange={value =>
              onFeatureChange((value as TrendSignalKey) || 'HR')
            }
            w={150}
          />
        </Stack>
        <Tooltip multiline w={280} label={TREND_NORMALIZATION_TOOLTIP}>
          <div>
            <Switch
              checked={normalizationEnabled}
              onChange={event =>
                onNormalizationChange(event.currentTarget.checked)
              }
              label='Normalizzazione'
            />
          </div>
        </Tooltip>
        <Tooltip multiline w={280} label={TREND_LOW_PASS_TOOLTIP}>
          <div>
            <Switch
              checked={lowPassEnabled}
              onChange={event => onLowPassChange(event.currentTarget.checked)}
              label='Filtro passabasso'
            />
          </div>
        </Tooltip>
        <Switch
          checked={showOverlaySessions}
          onChange={event => onShowOverlayChange(event.currentTarget.checked)}
          label='Mostra sessioni overlay'
        />
        <MultiSelect
          placeholder={overlayCompactValueLabel ?? 'Sessioni overlay'}
          variant='filled'
          leftSection={<Filter size={14} />}
          rightSection={<ChevronDown size={14} />}
          data={overlaySessionSelectData}
          value={overlayMultiSelectValue}
          onChange={onOverlaySessionChange}
          searchable
          clearable
          disabled={!showOverlaySessions}
          maxDropdownHeight={320}
          styles={{
            pill: {
              display: shouldCompactOverlayPills ? 'none' : undefined,
            },
            inputField: {
              minWidth: shouldCompactOverlayPills ? '100%' : undefined,
            },
          }}
          w={320}
        />
      </Group>
    </Card>
  );
}

export const buildOverlaySessionSelectData = (
  sessions: Array<{ sessionId: number; patientCode?: string | null }>
) => [
  { value: OVERLAY_SELECT_ALL_VALUE, label: 'Seleziona tutte' },
  ...sessions.map(session => ({
    value: session.sessionId.toString(),
    label: `${session.sessionId} · ${session.patientCode ?? 'n/a'}`,
  })),
];

export const handleOverlaySessionSelection = (
  values: string[],
  allSessionIds: string[],
  onChange: (values: string[]) => void
) => {
  if (values.includes(OVERLAY_SELECT_ALL_VALUE)) {
    onChange(allSessionIds);
    return;
  }

  onChange(values);
};
